/** Taking a stint away with you.
 *
 * The service has no export endpoint and does not need one: the portal already
 * holds every lap's timing for the session on screen, and what a driver wants
 * from "Export laps" is the table they are looking at, in something a
 * spreadsheet will open. So this writes that table and hands it over, with no
 * round trip and nothing for the service to maintain.
 *
 * Times are written in milliseconds rather than as `1:38.220`. The screen
 * formats for reading down a column; a file is for arithmetic, and a
 * spreadsheet that has to parse minutes and seconds back out of a string is a
 * spreadsheet that will get it wrong. The lap documents — the samples and the
 * racing line — are deliberately not in here: they are megabytes each and they
 * are fetched one at a time for exactly that reason.
 */

import type { DriveSession, LapTiming } from '@/lib/types';
import { bestLapMs, deltaToBestMs, sectorCount } from '@/components/sessions/derive';

/** Anything a field could carry that would break a comma-separated line. */
function cell(value: string | number | null): string {
  if (value === null) return '';
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The lap table as CSV, one row per lap the service holds. */
export function lapsCsv(session: DriveSession, laps: readonly LapTiming[]): string {
  const width = Math.max(sectorCount(laps), ...laps.map((lap) => lap.sector_ms.length), 0);
  const best = bestLapMs(laps);

  const header = [
    'lap',
    'lap_time_ms',
    ...Array.from({ length: width }, (_unused, index) => `sector_${index + 1}_ms`),
    'delta_best_ms',
    'valid',
    'samples',
    'recording',
    'received_at',
    'official_id',
    'car',
  ];

  const rows = [...laps]
    .sort((a, b) => a.lap - b.lap)
    .map((lap) =>
      [
        lap.lap,
        lap.lap_time_ms,
        ...Array.from({ length: width }, (_unused, index) => lap.sector_ms[index] ?? null),
        deltaToBestMs(lap, best),
        lap.valid ? 'true' : 'false',
        lap.samples,
        lap.stored === false ? 'removed' : 'kept',
        lap.received_at,
        session.official_id,
        session.car,
      ].map(cell),
    );

  return [header.map(cell).join(','), ...rows.map((row) => row.join(','))].join('\n') + '\n';
}

/** `trial-mountain-2026-09-17-laps.csv`, or a sensible stand-in when the
 *  session never named a layout. */
export function lapsFilename(session: DriveSession): string {
  const layout = session.official_id || session.track_name || 'session';
  const day = session.started_at.slice(0, 10);
  const safe = layout.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${safe || 'session'}-${day}-laps.csv`;
}

/** The analysis document's name on disk, beside the laps' CSV. */
export function analysisFilename(session: DriveSession): string {
  return lapsFilename(session).replace(/-laps\.csv$/, '-analysis.json');
}

/**
 * Hand the file to the browser.
 *
 * An object URL rather than a `data:` one: a long stint is past what some
 * browsers accept in a URL, and the blob is revoked on the next turn of the
 * event loop, so nothing is left holding the string.
 */
export function downloadLaps(session: DriveSession, laps: readonly LapTiming[]): void {
  const blob = new Blob([lapsCsv(session, laps)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = lapsFilename(session);
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
